import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro, fmtNum } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { ChevronLeft, ChevronRight, Check, X, Send, ChevronDown, Timer } from "lucide-react";

const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
};

const STATUS = {
  approved: { label: "Εγκρίθηκε", cls: "bg-[#EEF0EA] text-[#6E7A63]" },
  pending: { label: "Προς έγκριση", cls: "bg-[#FEF3C7] text-[#92400E]" },
  rejected: { label: "Απορρίφθηκε", cls: "bg-[#FEE2E2] text-[#991B1B]" },
  eligible: { label: "Χωρίς αίτημα", cls: "bg-[#F1EDE6] text-[#6F6A60]" },
  none: { label: "Εντός προγράμματος", cls: "bg-white text-[#8A8577] border border-[#E3DED4]" },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.none;
  return <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
};

export default function Overtime() {
  const [week, setWeek] = useState(mondayOf(new Date()));
  const [open, setOpen] = useState(null);
  const [hours, setHours] = useState({});
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["overtime-week", week],
    queryFn: async () => (await api.get(`/overtime/week?week_start=${week}`)).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["overtime-week"] });

  const request = useMutation({
    mutationFn: async () => (await api.post(`/overtime/week/request?week_start=${week}`)).data,
    onSuccess: (r) => {
      refresh();
      toast.success(r.created ? `${r.created} αιτήματα υπερωριών δημιουργήθηκαν` : "Δεν βρέθηκαν νέες υπερωρίες");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const decide = useMutation({
    mutationFn: async ({ id, status, approved_hours }) =>
      (await api.post(`/overtime/${id}/decide`, { status, approved_hours })).data,
    onSuccess: (_, v) => { refresh(); toast.success(v.status === "approved" ? "Οι υπερωρίες εγκρίθηκαν" : "Οι υπερωρίες απορρίφθηκαν"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const shift = (d) => {
    const x = new Date(week);
    x.setDate(x.getDate() + d);
    setWeek(mondayOf(x));
  };

  const rows = data?.rows || [];
  const t = data?.totals || {};
  const canApprove = data?.can_approve;
  const pending = rows.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="overtime-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Έλεγχος ωρών</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Υπερωρίες & Έγκριση</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Εβδομάδα {data?.week_start} – {data?.week_end}. Σύγκριση προγράμματος με πραγματικές ώρες ωρολογίου.
            Μόνο οι εγκεκριμένες υπερωρίες περνούν στη μισθοδοσία.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ot-week-prev" onClick={() => shift(-7)} className="h-10 w-10 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center hover:border-[#B9AE9A]">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input data-testid="ot-week-picker" type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))}
            className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#45423C]" />
          <button data-testid="ot-week-next" onClick={() => shift(7)} className="h-10 w-10 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center hover:border-[#B9AE9A]">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button data-testid="ot-request-btn" onClick={() => request.mutate()} disabled={request.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90">
            <Send className="h-4 w-4" /> {request.isPending ? "…" : canApprove ? "Αιτήματα εβδομάδας" : "Αίτηση υπερωριών"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[["Προγραμματισμένες", `${fmtNum(t.scheduled_hours)} ώ.`],
          ["Πραγματικές", `${fmtNum(t.actual_hours)} ώ.`],
          ["Υπερωρίες", `${fmtNum(t.overtime_hours)} ώ.`],
          ["Εγκεκριμένες", `${fmtNum(data?.approved_hours)} ώ.`],
          ["Κόστος υπερωριών", fmtEuro(t.overtime_amount)]].map(([label, val]) => (
          <div key={label} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`ot-kpi-${label}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{label}</div>
            <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{val}</div>
          </div>
        ))}
      </div>

      {canApprove && pending > 0 && (
        <div className="rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] px-5 py-4 text-sm text-[#92400E]" data-testid="ot-pending-banner">
          {pending} αίτημα/αιτήματα υπερωριών περιμένουν την έγκρισή σας για αυτή την εβδομάδα.
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <Timer className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Πρόγραμμα vs Πραγματικές ώρες</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Υπάλληλος</th>
                <th className="px-4 py-3">Τμήμα / Θέση</th>
                <th className="px-4 py-3 text-right">Προγραμμ.</th>
                <th className="px-4 py-3 text-right">Πραγματικές</th>
                <th className="px-4 py-3 text-right">Διαφορά</th>
                <th className="px-4 py-3 text-right">Υπερωρίες</th>
                <th className="px-4 py-3 text-right">Εγκεκρ.</th>
                <th className="px-4 py-3">Κατάσταση</th>
                <th className="px-4 py-3 text-right">Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {rows.map((r) => {
                const a = r.approval;
                const isOpen = open === r.employee_name;
                const val = hours[r.employee_name] ?? (a?.approved_hours ?? r.overtime_hours);
                return (
                  <React.Fragment key={r.employee_name}>
                    <tr className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]" data-testid={`ot-row-${r.employee_name}`}>
                      <td className="px-4 py-2">
                        <button data-testid={`ot-expand-${r.employee_name}`} onClick={() => setOpen(isOpen ? null : r.employee_name)}
                          className="inline-flex items-center gap-1.5 font-medium text-[#45423C]">
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          {r.employee_name}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-[#6F6A60]">{r.department || "—"}{r.position ? ` · ${r.position}` : ""}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtNum(r.scheduled_hours)}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtNum(r.actual_hours)}</td>
                      <td className={`px-4 py-2 text-right tabular font-medium ${r.diff_hours < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>
                        {r.diff_hours > 0 ? "+" : ""}{fmtNum(r.diff_hours)}
                      </td>
                      <td className="px-4 py-2 text-right tabular font-semibold text-[#C86235]">{fmtNum(r.overtime_hours)}</td>
                      <td className="px-4 py-2 text-right tabular font-semibold">
                        {a?.status === "approved" ? `${fmtNum(a.approved_hours)} ώ.` : "—"}
                      </td>
                      <td className="px-4 py-2"><Badge status={r.status} /></td>
                      <td className="px-4 py-2">
                        {canApprove && a?.status === "pending" ? (
                          <div className="flex items-center justify-end gap-2">
                            <input data-testid={`ot-hours-${r.employee_name}`} type="number" step="0.25" min="0" max={r.overtime_hours}
                              value={val}
                              onChange={(e) => setHours((h) => ({ ...h, [r.employee_name]: e.target.value }))}
                              className="w-20 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-xs outline-none focus:border-[#45423C]" />
                            <button data-testid={`ot-approve-${r.employee_name}`}
                              onClick={() => decide.mutate({ id: a.id, status: "approved", approved_hours: Number(val) })}
                              className="inline-flex items-center gap-1 rounded-lg bg-[#6E7A63] text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90">
                              <Check className="h-3.5 w-3.5" /> Έγκριση
                            </button>
                            <button data-testid={`ot-reject-${r.employee_name}`}
                              onClick={() => decide.mutate({ id: a.id, status: "rejected" })}
                              className="inline-flex items-center gap-1 rounded-lg border border-[#991B1B]/30 text-[#991B1B] px-3 py-1.5 text-xs font-semibold hover:bg-[#FEF2F2]">
                              <X className="h-3.5 w-3.5" /> Απόρριψη
                            </button>
                          </div>
                        ) : (
                          <div className="text-right text-xs text-[#8A8577]">
                            {a?.decided_by ? `${a.decided_by} · ${(a.decided_at || "").slice(0, 10)}` : "—"}
                          </div>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#F7F5F1]" data-testid={`ot-detail-${r.employee_name}`}>
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
                            {r.days.map((d) => (
                              <div key={d.date} className="rounded-xl border border-[#E3DED4] bg-white p-3">
                                <div className="text-[11px] font-semibold text-[#45423C]">{d.dow}</div>
                                <div className="text-[10px] text-[#8A8577]">{d.date.slice(5)}</div>
                                <div className="mt-2 text-xs text-[#6F6A60]">
                                  Πρόγρ.: <span className="tabular font-medium text-[#45423C]">{d.on_leave ? "Άδεια" : d.off ? "Ρεπό" : `${fmtNum(d.scheduled_hours)} ώ.`}</span>
                                </div>
                                <div className="text-xs text-[#6F6A60]">
                                  Πραγμ.: <span className="tabular font-medium text-[#45423C]">{fmtNum(d.actual_hours)} ώ.</span>
                                </div>
                                {d.diff_hours > 0 && (
                                  <div className="mt-1 text-[11px] font-semibold text-[#C86235] tabular">+{fmtNum(d.diff_hours)} ώ.</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[#6F6A60]">Δεν υπάρχουν προγράμματα βαρδιών για αυτή την εβδομάδα.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
